"use client";

import { applyMailAction, listMessageIdsPaged, type MailAction } from "./gmail";
import { cleanupHistoryStore } from "./stores";
import type { BulkActionType } from "./types";

const ESTIMATED_AVG_MESSAGE_BYTES = 75000;

function toMailAction(actionType: BulkActionType): MailAction {
  switch (actionType) {
    case "archive":
    case "keepNewest":
      return "archive";
    case "trash":
      return "trash";
    case "markRead":
      return "markRead";
    case "markUnread":
      return "markUnread";
    case "star":
      return "star";
    case "unstar":
      return "unstar";
    case "moveToLabel":
      return "moveToLabel";
  }
}

/** Runs [actionType] against every message matching [query] (paginated, capped
 *  at 1000), reporting progress via [onProgress]. Mirrors the app's
 *  BulkActionRunner — same query-driven, whole-mailbox semantics, not just a
 *  sample. Returns the number of messages processed. */
export async function runBulkAction({
  accessToken,
  query,
  actionType,
  historyLabel,
  moveToLabelName,
  onProgress,
}: {
  accessToken: string;
  query: string;
  actionType: BulkActionType;
  historyLabel: string;
  moveToLabelName?: string;
  onProgress?: (processed: number, total: number) => void;
}): Promise<number> {
  let ids = await listMessageIdsPaged(accessToken, query, 1000);

  if (actionType === "keepNewest") {
    // Gmail returns newest-first by default, so the first id is the one to keep.
    ids = ids.slice(1);
  }

  const mailAction = toMailAction(actionType);
  for (let i = 0; i < ids.length; i += 1) {
    try {
      await applyMailAction(accessToken, ids[i], mailAction, moveToLabelName);
    } catch {
      // skip messages that fail rather than aborting the whole run
    }
    onProgress?.(i + 1, ids.length);
  }

  if (actionType === "archive" || actionType === "trash" || actionType === "keepNewest") {
    cleanupHistoryStore.add({
      date: new Date().toISOString(),
      category: historyLabel,
      emailsCleaned: ids.length,
      bytesFreed: ids.length * ESTIMATED_AVG_MESSAGE_BYTES,
    });
  }

  return ids.length;
}
